import React from 'react';
import PropTypes from 'prop-types';
import {useMutation} from '@apollo/client';
import {Header} from 'semantic-ui-react';

import {Authorization} from '@appComponents/Authorization';
import {IconWithTooltip} from '@appComponents/IconCollection';
import {StartArgosJob, StopArgosJob, ReenqueueArgosJob} from '@graphql/argosJob/mutation';
import {ARGOS_JOB} from '@constants/resources';
import {SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE} from '@constants/variables';
import {showMessageBox} from '@utils/messageBox';
import {hideModal, showModalConfirmation} from '@utils/modal';
import {
    canEnableStopArgosJob,
    canShowReenqueueArgosJob,
    canShowStopArgosJob,
    canStartArgosJob,
    getStopDisabledTooltip,
} from '@modules/events/utils/argosJobActionRules';

const argosJobUpdateAuthorization = {
    [ARGOS_JOB]: SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE,
};

const popupProps = {position: 'top center'};

export const ArgosJobStartStopButtons = ({
    job,
    messageBoxName = 'argosJobMessage',
    onSuccess,
}) => {
    const [startArgosJobMutation] = useMutation(StartArgosJob);
    const [stopArgosJobMutation] = useMutation(StopArgosJob);
    const [reenqueueArgosJobMutation] = useMutation(ReenqueueArgosJob);

    const runMutation = (mutation, successMessage) => {
        return mutation({variables: {id: job.id}})
            .then(() => {
                showMessageBox(messageBoxName, successMessage, null, 'success');

                if (onSuccess) {
                    onSuccess();
                }
            })
            .catch((error) => {
                showMessageBox(messageBoxName, `Error: ${error.message}`, null, 'error');
            });
    };

    const confirmAction = (title, text, mutation, successMessage) => {
        showModalConfirmation({
            header: <Header content={title}/>,
            text,
            onYes: () => {
                hideModal();

                return runMutation(mutation, successMessage);
            },
        });
    };

    const showStart = canStartArgosJob(job);
    const showStop = canShowStopArgosJob(job);
    const enableStop = canEnableStopArgosJob(job);
    const showReenqueue = canShowReenqueueArgosJob(job);

    if (!showStart && !showStop && !showReenqueue) {
        return null;
    }

    return (
        <Authorization authorization={argosJobUpdateAuthorization}>
            <>
                {showStart && (
                    <IconWithTooltip
                        name='play'
                        tooltip='Start Argos job'
                        popupProps={popupProps}
                        onClick={() => confirmAction(
                            'Start Argos job',
                            'Are you sure you want to start this Argos job?',
                            startArgosJobMutation,
                            'Argos job start has been requested.'
                        )}
                    />
                )}
                {showStop && (
                    <IconWithTooltip
                        name='stop'
                        tooltip={enableStop ? 'Stop Argos job' : getStopDisabledTooltip(job)}
                        popupProps={popupProps}
                        iconProps={enableStop ? undefined : {disabled: true}}
                        onClick={enableStop
                            ? () => confirmAction(
                                'Stop Argos job',
                                'Are you sure you want to stop this Argos job?',
                                stopArgosJobMutation,
                                'Argos job stop has been requested.'
                            )
                            : undefined}
                    />
                )}
                {showReenqueue && (
                    <IconWithTooltip
                        name='redo'
                        tooltip='Re-enqueue stream'
                        popupProps={popupProps}
                        onClick={() => confirmAction(
                            'Re-enqueue stream',
                            'Are you sure you want to re-enqueue streams for this Argos job?',
                            reenqueueArgosJobMutation,
                            'Argos job stream re-enqueue has been requested.'
                        )}
                    />
                )}
            </>
        </Authorization>
    );
};

ArgosJobStartStopButtons.propTypes = {
    job: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        job_uuid: PropTypes.string,
        state: PropTypes.string,
        live_job: PropTypes.shape({
            status: PropTypes.shape({
                state: PropTypes.string,
            }),
            stream: PropTypes.shape({
                status: PropTypes.string,
            }),
        }),
    }).isRequired,
    messageBoxName: PropTypes.string,
    onSuccess: PropTypes.func,
};
